import type { ReportCardData, ResultsTableData, RileyMode, TraceLineProps } from '@patchbay/ui'
import type * as React from 'react'
import type { StructuredCaseSummary } from '../case-summary'

export type RileyRole = 'manager' | 'cover' | 'tech' | 'billing'
export type RileyAudience = 'customer' | 'contractor' | 'vendor' | 'internal' | 'unclassified'
export type RileyExecutor = 'riley' | 'patchbay'
export type RileyCardState = 'proposed' | 'confirmed' | 'edited_confirmed' | 'dismissed' | 'expired' | 'failed'
export type RileyChatRole = 'user' | 'riley' | 'system' | 'tool'
export type RileyChatScope = 'case' | 'queue' | 'dispatch' | 'ledger' | 'billing'

export interface RileyWalkthrough {
  id: string
  state: 'active' | 'paused' | 'completed'
  source_kind: 'lane' | 'filter'
  source_label: string
  filters: Record<string, unknown>
  position: number
  total: number
  current_case?: { case_id: string; case_number: string; href: string; label: string } | null
  can_back?: boolean
  briefed?: boolean
  thread_id?: string | null
  paused_at?: string | null
  completed_at?: string | null
}

export type CitationType =
  | 'desk_thread'
  | 'desk_comment'
  | 'conduit_note'
  | 'conduit_record'
  | 'kb'
  | 'unifi'
  | 'pbx'
  | 'ninja'
  | 'cambium'
  | 'zabbix'
  | 'books_invoice'
  | 'triage_log'
  | 'riley_writeback'

export interface RileyCitation {
  type: CitationType | string
  id: string
  label: string
  internal_only: boolean
  url?: string | null
}

export interface RileyUsage {
  requests?: number
  input?: number
  cached_input?: number
  output?: number
  reasoning?: number
  total?: number
  [key: string]: number | undefined
}

/** A streamed read reduced to a safe human label; the raw request/result is opt-in detail only. */
export interface RileyToolStep {
  seq?: number
  /** Internal stream tool name used only to keep the human label stable when its result arrives. */
  tool?: string
  label: string
  ok: boolean
  detail?: string
}

export interface RileyCardPreviewRow {
  label: string
  value: string
}

export interface RileyCardPreviewDiff {
  label: string
  before: string
  after: string
}

export interface RileyCardPreview {
  rows?: RileyCardPreviewRow[]
  body?: string
  note?: string
  diffs?: RileyCardPreviewDiff[]
  link?: string
  [key: string]: unknown
}

/** Card kinds that carry a reply Riley wrote. They open in the reply box; they do not send from the card (#746). */
export const REPLY_CARD_KINDS: ReadonlySet<string> = new Set(['send_reply', 'draft_reply'])

export const isReplyCard = (card: { kind: string }): boolean => REPLY_CARD_KINDS.has(card.kind)

export interface RileyCard {
  id: string
  kind: string
  title?: string | null
  audience: RileyAudience
  executor: RileyExecutor
  impact_text: string
  preview: RileyCardPreview
  payload: Record<string, unknown>
  scrub_report?: Record<string, number> | null
  state: RileyCardState
  can_confirm: boolean
  /** Dismiss remains available for a structurally invalid proposal whose Confirm is blocked. */
  can_dismiss?: boolean
  /** Structural proposal errors. Riley must issue a corrected card; the client cannot override these. */
  blocking_errors?: Array<{ code: string; message: string; field?: string | null; allowed?: unknown[] }>
  /** Server capability only; the UI still requires a typed editor (or an explicit superadmin debug gate). */
  editable?: boolean
  result?: Record<string, unknown> | null
  decided_at?: string | null
  /** A card Riley proposes expires 24 h after it is proposed (round 13); null = no expiry. */
  expires_at?: string | null
  /** Undo dismiss is offered (dismissed, not expired, not replaced, Riley never told; patchbay#198). */
  can_undismiss?: boolean
  /** The case the card belongs to (its task links come from `GET /cases/{id}/links/cards/{card id}`; #274). */
  case_id?: string | null
  /** Riley's own card id when Riley proposed it ("Riley can do this"; #274). */
  riley_card_id?: string | null
}

export interface RileyChatMessage {
  id: string
  role: RileyChatRole
  author?: string | null
  content_md: string
  citations: RileyCitation[]
  result_tables?: ResultsTableData[]
  report_cards?: ReportCardData[]
  card_ids: string[]
  created_at: string
  usage?: RileyUsage | null
  /** Client-side stream trace. It is collapsed after the answer arrives and is never required from the API. */
  tool_steps?: RileyToolStep[]
}

export interface RileyChatThread {
  id: string
  scope: RileyChatScope
  case_id?: string | null
  /** Patchbay returns the persisted local thread with this degraded state when Riley does not answer. */
  riley_state?: 'ok' | 'unreachable' | 'not_configured' | 'busy'
  riley_detail?: string | null
  summary_md?: string | null
  messages: RileyChatMessage[]
  cards: RileyCard[]
  walkthrough?: RileyWalkthrough | null
}

export type RileyChatStreamEvent =
  | { event: 'turn.started'; data: { turn_id: string; message_id?: string; model?: string } }
  | { event: 'tool.call'; data: { seq: number; name: string; verb?: string | null; args_summary?: string } }
  | {
      event: 'tool.result'
      data: { seq: number; ok: boolean; summary?: string; citations?: RileyCitation[]; ms?: number }
    }
  | { event: 'delta'; data: { text: string } }
  | { event: 'card'; data: { card: RileyCard } }
  | {
      event: 'turn.completed'
      data: {
        message_id: string
        reply: string
        citations?: RileyCitation[]
        result_tables?: ResultsTableData[]
        report_cards?: ReportCardData[]
        walkthrough?: RileyWalkthrough | null
        usage?: RileyUsage
        seconds?: number
      }
    }
  | { event: 'error'; data: { code: string; detail: string } }

export interface InvestigationTraceRead {
  seq: number
  source: 'conduit' | 'unifi' | 'pbx' | 'ninja' | 'cambium' | 'upstream' | 'desk' | 'related' | 'kb' | 'crm' | 'books'
  verb: string
  target_label?: string
  ok: boolean
  summary: string
  citation?: RileyCitation | null
  ms?: number
}

export interface InvestigationResult {
  found: boolean
  summary: string
  key_facts: string[]
  recommendation: string
  citations: string[]
  confidence: number
  draft_conflict?: string
  promise_outcome?: string
}

export type InvestigationStreamEvent =
  | { event: 'queued'; data: { position: number } }
  | { event: 'started'; data: { investigation_id: string; model?: string } }
  | { event: 'trace'; data: InvestigationTraceRead }
  | { event: 'result'; data: InvestigationResult }
  | { event: 'usage'; data: RileyUsage }
  | { event: 'done'; data: { caps_hit: 'tool_calls' | 'seconds' | null; seconds: number } }
  | { event: 'error'; data: { code: string; detail: string } }

/** Patchbay's durable copy, read after a stream disconnect or browser-side timeout. */
export interface StoredInvestigation {
  id: string
  case_id: string
  status: 'queued' | 'running' | 'done' | 'failed'
  findings_md: string | null
  result: InvestigationResult | null
  error: { code?: string; detail?: string } | null
  trace: InvestigationTraceRead[]
  caps_hit: 'tool_calls' | 'seconds' | null
  usage: RileyUsage | null
  started_at: string | null
  ended_at: string | null
  seconds: number | null
}

export interface RileyFeedbackTarget {
  target: 'message' | 'card' | 'investigation'
  id: string
  rating: 'up' | 'down'
  /** The optional "what was wrong?" on a thumbs-down. */
  note?: string
}

/** The signed-in person's current rating of one object (the latest wins), so a thumb stays pressed after a reload. */
export interface RileyRating {
  target: RileyFeedbackTarget['target']
  id: string
  rating: 'up' | 'down'
  note?: string | null
}

/** The honest state of a confirmed card whose work finishes later (a re-triage): derived from the case's events. */
export interface RileyCardProgress {
  card_id: string
  state: 'running' | 'done' | 'failed'
  headline: string
  expected?: string | null
  items: { kind: 'note' | 'suggestion' | 'verdict'; label: string; target?: string | null }[]
}

/** Card kinds whose confirm starts work that lands later (mirrors the API's `ASYNC_KINDS`). */
export const ASYNC_CARD_KINDS: ReadonlySet<string> = new Set(['retriage'])

export type ChatDelivery = RileyChatThread | AsyncIterable<RileyChatStreamEvent>

/**
 * Transport seam for the generated client owned by web-shell. This slice never talks to Riley
 * directly: the implementation must call Patchbay's /api/v1 proxy with the user's cookie + CSRF.
 */
export interface RileyRailDataSource {
  startChat(input: { case_id?: string; desk_ticket_id?: string; scope: RileyChatScope; resume: boolean }): Promise<RileyChatThread>
  postMessage(threadId: string, input: { message: string; client_message_id: string }): Promise<ChatDelivery>
  /** `acknowledged` = guard warning codes the person read (warn-then-allow); a 409 warnings_unacknowledged asks. */
  confirmCard(cardId: string, editedPayload?: Record<string, unknown>, acknowledged?: string[]): Promise<RileyCard>
  /** #838 dry-run rewrite for a close-out field. It neither confirms the card nor sends anything. */
  tidyServiceHistory?(cardId: string, text: string): Promise<{ text: string; stripped?: string[] }>
  dismissCard(cardId: string, reason?: string): Promise<RileyCard>
  /**
   * #746: move a Riley reply card into the case's reply box as a suggestion (nothing is sent) and return the card,
   * now dismissed. The reply is then edited and sent from the composer, the one human send surface.
   */
  moveToReplyBox?(cardId: string): Promise<RileyCard>
  /** Undo dismiss: the card is proposed again; nothing runs (patchbay#198). */
  undismissCard?(cardId: string): Promise<RileyCard>
  /** Undo successful reversible rows from a completed bulk card within its 24-hour window. */
  undoBulkCard?(cardId: string): Promise<RileyCard>
  /** Restore unchanged fields from a completed Riley triage packet within its 24-hour window. */
  undoTriageCard?(cardId: string): Promise<RileyCard>
  /** Undo a staff-instructed Riley write. The API returns the action row, not a card (#1097). */
  undoStaffAction?(actionId: string): Promise<{ id: string; state: string }>
  /** GET the stored thread (side-effect free): used to pick up an answer whose stream was lost (patchbay#196). */
  getChat?(threadId: string): Promise<RileyChatThread>
  investigate(
    caseId: string,
    input: { question?: string; max_tool_calls: number; max_seconds: number },
  ): AsyncIterable<InvestigationStreamEvent>
  /** Side-effect-free recovery of this exact run when its SSE stream is lost. */
  getInvestigation?(investigationId: string): Promise<StoredInvestigation>
  submitFeedback?(feedback: RileyFeedbackTarget): Promise<void>
  /** The person's current ratings for these ids (side-effect free). */
  getFeedback?(ids: string[]): Promise<RileyRating[]>
  /** Running / done / failed for a confirmed async card (null = not an async card). */
  cardProgress?(cardId: string): Promise<RileyCardProgress | null>
}

export interface RileyRailFeatureProps {
  caseId: string
  deskTicketId?: string
  caseSummary?: StructuredCaseSummary
  onBuildSummary?: () => Promise<void>
  /** Bring a place on the case into view (`turn:<id>`): a finished card's "Jump to the new note". */
  onFocusTarget?: (target: string) => void
  dataSource: RileyRailDataSource
  context?: React.ReactNode
  initialThread?: RileyChatThread
  initialTrace?: TraceLineProps[]
  /** The signed-in person's id: the hypothesis fold and hidden cards are remembered per person. */
  viewerId?: string
  viewerRole: RileyRole
  mode: RileyMode
  onModeChange: (mode: RileyMode) => void
}
